import React from 'react'
import PDTable from './components/PDTable'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { Controller, useForm } from 'react-hook-form'
import { DatePicker, Input, PolicyDispatchingPermission, Select, assets } from '@pulseops/common'
import { ActivityIndicator, View, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import {
  borderedDatePickerContainerStyle,
  borderedInputStyle,
  borderedSelectStyle
} from './styles/policyDispatchingStyles'
import { CheckLogSearchResponse, PolicyDispatchingService } from './services/PolicyDispatchingService'
import {
  formatDate,
  getActionLabel,
  getPermissions,
  getScreenLabel,
  getStatusLabel
} from './utils/policyDispatchingUtils'

const headCell = [
  { id: 1, label: 'Số Log' },
  { id: 2, label: 'Số hợp đồng' },
  { id: 3, label: 'Màn hình' },
  { id: 4, label: 'Hành động' },
  { id: 5, label: 'Tên đăng nhập' },
  { id: 6, label: 'Văn phòng' },
  { id: 7, label: 'Tình trạng' },
  { id: 8, label: 'Thời gian ' },
  { id: 9, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'no' },
  { id: 2, label: 'policyNumber' },
  { id: 3, label: 'step' },
  { id: 4, label: 'action' },
  { id: 5, label: 'userName' },
  { id: 6, label: 'office' },
  { id: 7, label: 'status' },
  { id: 8, label: 'time' },
  { id: 9, label: 'note' }
]

type CheckLogRow = {
  no: string
  policyNumber: string
  step: string
  action: string
  userName: string
  office: string
  status: string
  time: string
  note: string
}

type FormValues = {
  contractNumber: string
  fromDate: Date | null
  toDate: Date | null
  userName: string
  action: { label: string; value: string } | null
}

const toRow = (item: {
  action: string | null
  actionTime: string | null
  contractNumber: string | null
  logNo: string | null
  office: string | null
  remark: string | null
  status: string | null
  step: string | null
  userName: string | null
}): CheckLogRow => ({
  no: item.logNo || '',
  policyNumber: item.contractNumber || '',
  step: getScreenLabel(item.step) || '',
  action: getActionLabel(item.action) || '',
  userName: item.userName || '',
  office: item.office || '',
  status: getStatusLabel(item.status) || '',
  time: item.actionTime || '',
  note: item.remark || ''
})

const hasCheckLogBody = (
  response: CheckLogSearchResponse
): response is Extract<CheckLogSearchResponse, { body: unknown }> => 'body' in response

const typeOptions = [
  { label: 'Nhập kho', value: 'Stock in' },
  { label: 'Đổi ngày nhập kho', value: 'Change stock-in date' },
  { label: 'Đổi văn phòng', value: 'Change office' },
  { label: 'Trả lại', value: 'Give back' },
  { label: 'Tải về', value: 'Download' },
  { label: 'Giao hợp đồng', value: 'Deliver contract' },
  { label: 'Hủy giao', value: 'Cancel delivery' },
  { label: 'Điều chỉnh ngày giao', value: 'Change delivery date' },
  { label: 'Lưu', value: 'Save' },
  { label: 'Xóa', value: 'Delete' }
]

export default function PDCheckLogScreen() {
  const { control, handleSubmit, getValues } = useForm<FormValues>({
    defaultValues: {
      contractNumber: '',
      fromDate: null,
      toDate: null,
      userName: '',
      action: null
    }
  })
  const [rows, setRows] = React.useState<CheckLogRow[]>([])
  const [pageInfo, setPageInfo] = React.useState<{
    page: number
    totalPages: number
    totalElements: number
    first: boolean
    last: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.CheckLogSearch)

  const search = (values: FormValues, nextPage: number) => {
    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchCheckLogs({
        contractNumber: values.contractNumber,
        fromDate: formatDate(values.fromDate) || undefined,
        toDate: formatDate(values.toDate) || undefined,
        userName: values.userName || undefined,
        action: values.action?.value || undefined,
        page: nextPage
      }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!hasCheckLogBody(response)) {
            setRows([])
            setPageInfo(null)
            setIsLoading(false)
            setModalInfo({ title: 'Tìm kiếm', message: 'Không tìm được thông tin theo yêu cầu' })
            setOpenModal(true)
            return
          }

          setRows(response.body.items.map(toRow))
          setPageInfo(response.body.page)
          setIsLoading(false)
        })
      ),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to search check logs', error)
          setRows([])
          setPageInfo(null)
          setIsLoading(false)
          setModalInfo({ title: 'Tìm kiếm thất bại', message: 'Có lỗi xảy ra. Vui lòng thử lại sau' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => search(values, 0))

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Tra cứu nhật ký</Text>
      <View style={styles.inputWrapper}>
        <View style={styles.input}>
          <Controller
            name="contractNumber"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Số hợp đồng là bắt buộc'
              }
            }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Số hợp đồng'}
                required
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                maxLength={8}
                inputStyle={borderedInputStyle}
                filled
              />
            )}
          />
        </View>
        <View style={styles.input}>
          <Controller
            name="fromDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                alwaysShow
                label={'Từ ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                inputStyle={{ paddingBottom: 3 }}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
              />
            )}
          />
        </View>
        <View style={styles.input}>
          <Controller
            name="toDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                alwaysShow
                label={'Đến ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                inputStyle={{ paddingBottom: 3 }}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
              />
            )}
          />
        </View>

        <View style={styles.input}>
          <Controller
            name="userName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Tên đăng nhập'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                maxLength={1000}
                inputStyle={borderedInputStyle}
                filled
              />
            )}
          />
        </View>
        <View style={styles.input}>
          <Controller
            control={control}
            name={'action'}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Select
                label={'Hành động'}
                options={typeOptions}
                placeholder={'Chọn hành động'}
                onChange={(e) => {
                  onChange(e)
                }}
                onBlur={onBlur}
                value={value}
                key={value?.value}
                errorMessage={error?.message}
                containerStyles={borderedSelectStyle}
                filled
              />
            )}
          />
        </View>
        <View style={styles.input} />

        <View
          style={
            {
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: 10,
              marginTop: 20
            } as any
          }
        >
          <button
            type="button"
            style={allowSearch ? styles.submitButton : styles.disabledButton}
            onClick={handleSearch}
            disabled={isLoading || !allowSearch}
          >
            <span style={styles.mr_5}>
              <assets.SearchWhite />
            </span>
            Tìm
          </button>
        </View>
      </View>
      <View style={{ marginTop: 30 } as any}>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          pagination={
            pageInfo
              ? {
                  page: pageInfo.page,
                  totalPages: pageInfo.totalPages,
                  totalElements: pageInfo.totalElements,
                  first: pageInfo.first,
                  last: pageInfo.last,
                  onPageChange: (nextPage) => search(getValues(), nextPage)
                }
              : undefined
          }
        />
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap' as const,
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '16px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    gap: '20px 10px'
  },
  input: {
    width: '32.5%'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
